import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  clearIdentity,
  readIdentity,
  storeIdentity,
  enrollUser,
  unlockUser,
  changePin,
  removeVaultUser,
  listVaultUsers,
  isEnrolled,
  identityFromVault,
  isInfrastructureFailure,
  type StoredIdentity,
  type VaultRecord,
} from '@floorfile/offline'
import { ApiError, createClient } from '@floorfile/sdk'
import {
  clearImpersonationSession,
  readImpersonationSession,
  type ImpersonationDetails,
  type ImpersonationSession,
} from './impersonation'
import { apiBaseUrl, apiFetch } from './api-url'
import { shareInFlight } from './single-flight'
import { trackDbOutage } from './outage'
import { offlineRuntime } from './offline-runtime'
import { clearUserScopedStorage } from './user-storage'
import { sessionKnownEnded, writeSessionMarker } from './session-marker'

/**
 * Frontend auth architecture (SECURITY_&_UAC §9): the access token lives in
 * memory only (never localStorage); on boot we try POST /auth/refresh (the
 * httpOnly cookie authenticates it); silent refresh fires 2 minutes before
 * expiry; a REJECTED refresh hard-logs-out.
 *
 * Offline attribution (OFFLINE_&_MOBILE_RESILIENCE.md §9): the authenticated
 * IDENTITY — never a secret — persists locally. When boot refresh fails on a
 * NETWORK error (a dead-zone boot), the app enters offline-authenticated
 * mode under that identity: reads come from cache, writes queue in the
 * outbox, and reconnect re-auths (the httpOnly refresh cookie) before sync.
 */

export interface AuthUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'OWNER' | 'ADMIN' | 'SUPERVISOR' | 'MEMBER'
  /** Signing dialogs ask for a TOTP code when the signer has 2FA enrolled. */
  totpEnabled?: boolean
  /** Account lifecycle (contract `User.status`). The verification gate reads
   * it to tell an INVITED account from an unverified ACTIVE one
   * (FF-AUDIT-057); absent on an offline-rehydrated identity. */
  status?: 'INVITED' | 'ACTIVE' | 'DISABLED'
}

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated'
  user?: AuthUser
  permissions: ReadonlySet<string>
  /** Tenant id (JWT `cid`) — the offline runtime stamps queued ops with it. */
  companyId?: string
  /** True from an explicit `logout()` until the next session starts. The
   * sign-in guard then sends the next person to the dashboard instead of
   * back to the page the previous user left open — on a shared plant tablet
   * that page belongs to someone else (owner report, 2026-09-27). An expired
   * or rejected session leaves it false, so a deep link still returns there. */
  signedOut: boolean
  /** True while running on the persisted identity with no live token (D24). */
  offlineAuthenticated: boolean
  /** Company mandates 2FA and this user has not enrolled: the session is
   * enrollment-only — the app must show the 2FA setup gate (C-01). */
  twoFactorSetupRequired: boolean
  /** Self-registered owner has not yet consumed the emailed verification
   * token. The API issues a real but permission-empty session. */
  emailVerificationRequired: boolean
  /** Device-local offline-at-rest encryption (D24 §9): `enrolled` is whether
   * this user has a PIN-wrapped data key on THIS device; `unlocked` is
   * whether the codec is currently wired into the offline runtime for this
   * page session. A PIN unlocks the local key only — never a server
   * credential — so `unlocked` never implies a live/minted session. */
  offlineEncryption: { enrolled: boolean; unlocked: boolean }
  /** Company policy (`requireOfflineEncryption`): best-effort — cached in
   * localStorage per company so the force-enroll gate still works in a dead
   * zone; refreshed whenever the shell's navigation settings load. */
  requireOfflineEncryption: boolean
  /** FF-QA-003: `CompanyConfigProvider` hands over the company policy it
   * received with `GET /settings/navigation`, so the flag costs no request
   * of its own. Not for feature code. */
  applyCompanyPolicy: (policy: { requireOfflineEncryption?: boolean | undefined }) => void
  /** Enable offline encryption for the current user on this device: mints a
   * PIN-wrapped data key and wires the codec in immediately. */
  enableOfflineEncryption: (pin: string) => Promise<void>
  /** Unlock the current user's already-enrolled data key with their PIN.
   * Throws on a wrong PIN — never mints or touches a server session. */
  unlockOffline: (pin: string) => Promise<void>
  /** Re-wrap the current user's data key under a new PIN. Throws on a wrong
   * old PIN. */
  changeOfflinePin: (oldPin: string, newPin: string) => Promise<void>
  /** Forget this device's offline encryption for the current user. Blocked
   * by the caller when the company forces `requireOfflineEncryption`. */
  disableOfflineEncryption: () => Promise<void>
  /**
   * Quick-switch to another user already enrolled on this device: unlocks
   * their data key with their PIN and applies their persisted identity.
   * This is OFFLINE-AUTHENTICATED for the switched user — it never
   * fabricates or reuses a server access token; syncing their queued work
   * still requires a real sign-in (`/login`).
   */
  switchUser: (userId: string, pin: string) => Promise<void>
  /** Users enrolled for offline encryption on this device (for the switcher). */
  listDeviceUsers: () => Promise<VaultRecord[]>
  /** Re-run the cookie refresh now (used after finishing 2FA enrollment to
   * promote the enrollment-only session to a full one). */
  refreshSession: () => Promise<boolean>
  impersonation?: ImpersonationDetails
  login: (
    email: string,
    password: string,
    turnstileToken?: string,
  ) => Promise<{ mfaRequired?: boolean; mfaToken?: string }>
  /** Public company self-registration — creates the OWNER and signs in. */
  registerCompany: (input: {
    companyName: string
    timezone: string
    owner: { firstName: string; lastName: string; email: string; password: string }
    turnstileToken?: string
  }) => Promise<void>
  /** Accept an emailed invitation (`/accept-invite?token=…`): sets name +
   * password, activates the account and signs in. Throws `ApiError` so the
   * page can key its copy on the error code (FF-AUDIT-058). */
  acceptInvite: (input: { token: string; firstName: string; lastName: string; password: string }) => Promise<void>
  completeMfa: (mfaToken: string, code: string) => Promise<void>
  logout: () => Promise<void>
  endImpersonation: () => void
  /** Authenticated fetch — attaches the bearer token; use for all API calls. */
  authFetch: (path: string, init?: RequestInit) => Promise<Response>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

interface TokenBundle {
  accessToken: string
  expiresIn: number
  user: AuthUser
  permissions: string[]
  twoFactorSetupRequired?: boolean
  emailVerificationRequired?: boolean
}

/** JWT payloads are not secret — decode the tenant claim for local scoping. */
function decodeCompanyId(accessToken: string): string | undefined {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1] ?? '')) as { cid?: string }
    return typeof payload.cid === 'string' ? payload.cid : undefined
  } catch {
    return undefined
  }
}

/** Best-effort cache of the company's `requireOfflineEncryption` policy, per
 * company, so the force-enroll gate can still make a decision in a dead
 * zone (D24 §9) — refreshed opportunistically whenever we're online. */
const REQUIRE_OFFLINE_ENCRYPTION_PREFIX = 'floorfile.offline.require-encryption:'

function readCachedRequireOfflineEncryption(companyId: string): boolean {
  try {
    return localStorage.getItem(REQUIRE_OFFLINE_ENCRYPTION_PREFIX + companyId) === 'true'
  } catch {
    return false
  }
}

function writeCachedRequireOfflineEncryption(companyId: string, value: boolean): void {
  try {
    localStorage.setItem(REQUIRE_OFFLINE_ENCRYPTION_PREFIX + companyId, String(value))
  } catch {
    /* storage unavailable (private mode etc.) — the in-memory state still works this session */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [signedOut, setSignedOut] = useState(false)
  const [user, setUser] = useState<AuthUser | undefined>(undefined)
  const [permissions, setPermissions] = useState<ReadonlySet<string>>(new Set())
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const [offlineAuthenticated, setOfflineAuthenticated] = useState(false)
  const [twoFactorSetupRequired, setTwoFactorSetupRequired] = useState(false)
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false)
  const [offlineEnrolled, setOfflineEnrolled] = useState(false)
  const [offlineUnlocked, setOfflineUnlocked] = useState(false)
  const [requireOfflineEncryption, setRequireOfflineEncryption] = useState(false)
  const tokenRef = useRef<string | undefined>(undefined)
  const impersonationRef = useRef<ImpersonationSession | undefined>(undefined)
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // `applyBundle` schedules the next refresh, and `refresh` → `performRefresh`
  // → `applyBundle` closes the loop, so `refresh` cannot be a dependency of
  // the callback that schedules it. The timer reads it through this ref,
  // synced right after `refresh` is defined.
  const refreshRef = useRef<() => Promise<boolean>>(() => Promise.resolve(false))
  // Which user we last computed `enrolled` for — so a silent token refresh
  // (same user) never resets `unlocked` and forces a needless re-PIN.
  const enrollmentCheckedForUserId = useRef<string | undefined>(undefined)

  const checkOfflineEnrollment = useCallback((userId: string) => {
    if (enrollmentCheckedForUserId.current !== userId) {
      enrollmentCheckedForUserId.current = userId
      setOfflineUnlocked(false)
    }
    void isEnrolled(offlineRuntime.db, userId)
      .then((enrolled) => setOfflineEnrolled(enrolled))
      .catch(() => setOfflineEnrolled(false))
  }, [])

  // FF-QA-003: the policy rides on `GET /settings/navigation`, which
  // `CompanyConfigProvider` already fetches for the nav on every boot (and
  // refetches on `settings.updated`) — auth used to make the same request a
  // second time, so every shell boot paid for it twice. The provider hands the
  // answer over here instead. Ignored under impersonation (the platform admin
  // is not a member of the company and is never forced to enrol) and before a
  // tenant is known; a failed load simply leaves the cached value standing.
  const companyIdRef = useRef<string | undefined>(undefined)
  const applyCompanyPolicy = useCallback((policy: { requireOfflineEncryption?: boolean | undefined }) => {
    const cid = companyIdRef.current
    if (!cid || impersonationRef.current || typeof policy.requireOfflineEncryption !== 'boolean') return
    setRequireOfflineEncryption(policy.requireOfflineEncryption)
    writeCachedRequireOfflineEncryption(cid, policy.requireOfflineEncryption)
  }, [])

  const applyBundle = useCallback((bundle: TokenBundle) => {
    writeSessionMarker(true)
    setSignedOut(false)
    impersonationRef.current = undefined
    clearImpersonationSession()
    tokenRef.current = bundle.accessToken
    setUser(bundle.user)
    setPermissions(new Set(bundle.permissions))
    const cid = decodeCompanyId(bundle.accessToken)
    companyIdRef.current = cid
    setCompanyId(cid)
    setOfflineAuthenticated(false)
    setTwoFactorSetupRequired(bundle.twoFactorSetupRequired === true)
    setEmailVerificationRequired(bundle.emailVerificationRequired === true)
    setStatus('authenticated')
    checkOfflineEnrollment(bundle.user.id)
    // The cached policy stands until the provider's navigation load answers
    // (`applyCompanyPolicy`), so the force-enrol gate decides at once.
    if (cid && bundle.emailVerificationRequired !== true) {
      setRequireOfflineEncryption(readCachedRequireOfflineEncryption(cid))
    }
    // Persist the identity (never the token) for dead-zone boots (D24 §9).
    // Enrollment-only sessions are never persisted as an offline identity.
    if (
      cid &&
      bundle.twoFactorSetupRequired !== true &&
      bundle.emailVerificationRequired !== true
    ) {
      storeIdentity({
        userId: bundle.user.id,
        companyId: cid,
        email: bundle.user.email,
        firstName: bundle.user.firstName,
        lastName: bundle.user.lastName,
        role: bundle.user.role,
        permissions: bundle.permissions,
        totpEnabled: bundle.user.totpEnabled,
      })
    }
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(
      () => void refreshRef.current(),
      Math.max((bundle.expiresIn - 120) * 1000, 30_000),
    )
  }, [checkOfflineEnrollment])

  const applyOfflineIdentity = useCallback((identity: StoredIdentity) => {
    setSignedOut(false)
    tokenRef.current = undefined
    setUser({
      id: identity.userId,
      email: identity.email,
      firstName: identity.firstName,
      lastName: identity.lastName,
      role: identity.role as AuthUser['role'],
      ...(identity.totpEnabled !== undefined ? { totpEnabled: identity.totpEnabled } : {}),
    })
    setPermissions(new Set(identity.permissions))
    companyIdRef.current = identity.companyId
    setCompanyId(identity.companyId)
    setOfflineAuthenticated(true)
    setTwoFactorSetupRequired(false)
    setEmailVerificationRequired(false)
    setStatus('authenticated')
    checkOfflineEnrollment(identity.userId)
    setRequireOfflineEncryption(readCachedRequireOfflineEncryption(identity.companyId))
  }, [checkOfflineEnrollment])

  const applyImpersonation = useCallback((session: ImpersonationSession) => {
    setSignedOut(false)
    impersonationRef.current = session
    tokenRef.current = session.token
    companyIdRef.current = undefined
    setUser(session.viewer)
    setPermissions(new Set(session.permissions))
    setStatus('authenticated')
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(() => {
      tokenRef.current = undefined
      impersonationRef.current = undefined
      clearImpersonationSession()
      setUser(undefined)
      setPermissions(new Set())
      setStatus('anonymous')
    }, Math.max(session.expiresAt - Date.now(), 0))
  }, [])

  const clearAuth = useCallback(() => {
    tokenRef.current = undefined
    impersonationRef.current = undefined
    clearImpersonationSession()
    setUser(undefined)
    setPermissions(new Set())
    companyIdRef.current = undefined
    setCompanyId(undefined)
    setOfflineAuthenticated(false)
    setTwoFactorSetupRequired(false)
    setEmailVerificationRequired(false)
    setStatus('anonymous')
    // The PIN-derived codec is page-session-only by design (never persisted)
    // — drop it here so every fresh sign-in / switch starts locked again.
    offlineRuntime.setCodec(undefined)
    setOfflineEnrolled(false)
    setOfflineUnlocked(false)
    enrollmentCheckedForUserId.current = undefined
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
  }, [])

  /**
   * SINGLE-FLIGHT. The refresh token ROTATES, and the server treats a second
   * use of an already-rotated token as theft: `TOKEN_REUSE` revokes the whole
   * family (SECURITY_&_UAC §3.2, auth.service.ts). That defense is correct —
   * but this client used to hand it a false positive.
   *
   * `refresh()` has three callers: the proactive expiry timer, the mount
   * effect, and `onUnauthorized`, which fires PER 401 RESPONSE. When an access
   * token lapses on a page holding several queries, every one of them 401s at
   * once and each calls refresh. They all read the same cookie before any
   * response sets the new one, so the first rotates and the rest replay a dead
   * token — the server revokes the family and the user is signed out mid-task,
   * with no way to tell it from a real session expiry.
   *
   * Sharing one in-flight promise makes the concurrent case a single request,
   * which is what the rotation scheme already assumes. Found 2026-07-31: it
   * dropped 3 of 10 spine specs to the sign-in page in CI (and once locally),
   * which read as unrelated "element(s) not found" failures.
   */
  const refreshInFlight = useRef<Promise<boolean> | null>(null)

  const performRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const res = await apiFetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'X-Requested-With': 'floorfile' },
        credentials: 'include',
      })
      // FF-AUDIT-088: a dead proxy or edge answers with a 5xx/timeout status,
      // not a thrown fetch — that is the dead-zone boot in another costume,
      // and it used to be read as "the server rejected the session" and sign
      // the person out, outbox and saved answers behind the login wall.
      const body = (await res.json().catch(() => undefined)) as { data?: TokenBundle } | undefined
      if (!res.ok && isInfrastructureFailure(res.status, body)) throw new Error(`unreachable (${res.status})`)
      if (!res.ok || !body?.data) {
        // The SERVER rejected the session (expired/revoked refresh token) —
        // hard logout; queued offline work stays in the outbox for the same
        // user's next sign-in (D24 §9).
        clearIdentity()
        clearAuth()
        writeSessionMarker(false)
        return false
      }
      applyBundle(body.data)
      return true
    } catch {
      // NETWORK failure — a dead-zone boot, not an auth rejection. Run on
      // the persisted identity; the next reconnect re-auths before sync.
      const identity = readIdentity()
      if (identity) {
        applyOfflineIdentity(identity)
      } else {
        clearAuth()
      }
      return false
    }
  }, [applyBundle, applyOfflineIdentity, clearAuth])

  const refresh = useCallback((): Promise<boolean> => {
    if (impersonationRef.current) return Promise.resolve(false)
    return shareInFlight(refreshInFlight, performRefresh)
  }, [performRefresh])
  useEffect(() => {
    refreshRef.current = refresh
  }, [refresh])

  useEffect(() => {
    // The platform-admin plane has its own provider and credential boundary.
    // Do not make a noisy tenant refresh request while rendering /admin.
    if (window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/')) {
      setStatus('anonymous')
      return undefined
    }
    const impersonation = readImpersonationSession()
    if (impersonation) applyImpersonation(impersonation)
    // FF-QA-002 / FF-QA-141: after a logout or a rejected refresh there is no
    // cookie to refresh, so the boot refresh could only 401 — skip it when the
    // marker says the session KNOWN-ended ('0'). An absent marker is unknown
    // and must refresh: browsers upgraded from the release before the marker
    // hold a live cookie and no marker, and an offline device only reaches its
    // persisted offline identity through refresh()'s network-failure path.
    // Skipping on "absent" signed out every upgraded user and stranded offline
    // devices on the sign-in screen. See `session-marker.ts`.
    else if (sessionKnownEnded()) setStatus('anonymous')
    else void refresh()
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current)
    }
  }, [applyImpersonation, refresh])

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string) => {
      clearAuth()
      const res = await apiFetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, ...(turnstileToken ? { turnstileToken } : {}) }),
      })
      const body = (await res.json()) as {
        data?: TokenBundle & { mfaRequired?: boolean; mfaToken?: string }
        error?: { message: string }
      }
      if (res.status === 202 && body.data?.mfaRequired) {
        return { mfaRequired: true, mfaToken: body.data.mfaToken ?? '' }
      }
      if (!res.ok || !body.data) {
        throw new Error(body.error?.message ?? 'Sign-in failed.')
      }
      applyBundle(body.data)
      return {}
    },
    [applyBundle, clearAuth],
  )

  const registerCompany = useCallback(
    async (input: {
      companyName: string
      timezone: string
      owner: { firstName: string; lastName: string; email: string; password: string }
      turnstileToken?: string
    }) => {
      clearAuth()
      const { turnstileToken, ...rest } = input
      const res = await apiFetch('/api/v1/companies/register', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...rest, ...(turnstileToken ? { turnstileToken } : {}) }),
      })
      const body = (await res.json()) as { data?: TokenBundle; error?: { message: string } }
      if (!res.ok || !body.data) throw new Error(body.error?.message ?? 'Registration failed.')
      applyBundle(body.data)
    },
    [applyBundle, clearAuth],
  )

  const acceptInvite = useCallback(
    async (input: { token: string; firstName: string; lastName: string; password: string }) => {
      clearAuth()
      const res = await apiFetch('/api/v1/auth/accept-invite', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })
      const body = (await res.json().catch(() => ({}))) as {
        data?: TokenBundle
        error?: { code: string; message: string }
      }
      if (!res.ok || !body.data) {
        throw new ApiError(res.status, body.error ?? { code: 'INTERNAL', message: `Request failed (${res.status})` })
      }
      applyBundle(body.data)
    },
    [applyBundle, clearAuth],
  )

  const completeMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const res = await apiFetch('/api/v1/auth/mfa', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfaToken, code }),
      })
      const body = (await res.json()) as { data?: TokenBundle; error?: { message: string } }
      if (!res.ok || !body.data) throw new Error(body.error?.message ?? 'Code not accepted.')
      applyBundle(body.data)
    },
    [applyBundle],
  )

  const logout = useCallback(async () => {
    if (!impersonationRef.current) {
      await apiFetch('/api/v1/auth/logout', { method: 'POST' }).catch(() => undefined)
    }
    writeSessionMarker(false)
    // Explicit logout drops the persisted identity (D24 §9 data-at-rest);
    // the AppShell also clears the user's cached partition.
    clearIdentity()
    // …and the per-user local preferences (audit FF-06). Saved views, nav pins
    // and view modes used to be global keys that survived logout, so on a
    // shared plant tablet the next operator inherited the previous one's
    // working set. Same posture as the identity and query-cache partitions
    // immediately above — this was the one store that was missing it.
    if (companyId && user) clearUserScopedStorage({ companyId, userId: user.id })
    // Set in the same batch as `clearAuth()`, so the guard's first anonymous
    // render already knows this was deliberate — its <Navigate> effect runs
    // after the shell's own navigate('/login') and would otherwise record the
    // page the previous user left open as the next sign-in's destination.
    setSignedOut(true)
    clearAuth()
  }, [clearAuth, companyId, user])

  const endImpersonation = useCallback(() => {
    clearAuth()
  }, [clearAuth])

  const enableOfflineEncryption = useCallback(
    async (pin: string) => {
      if (!user || !companyId) throw new Error('Sign in before enabling offline encryption.')
      const identity: Omit<StoredIdentity, 'storedAt'> = {
        userId: user.id,
        companyId,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        permissions: Array.from(permissions),
        ...(user.totpEnabled !== undefined ? { totpEnabled: user.totpEnabled } : {}),
      }
      const result = await enrollUser(offlineRuntime.db, identity, pin)
      offlineRuntime.setCodec(result.codec)
      setOfflineEnrolled(true)
      setOfflineUnlocked(true)
    },
    [user, companyId, permissions],
  )

  const unlockOffline = useCallback(
    async (pin: string) => {
      if (!user) throw new Error('Sign in before unlocking offline access.')
      const result = await unlockUser(offlineRuntime.db, user.id, pin) // throws on wrong PIN
      offlineRuntime.setCodec(result.codec)
      setOfflineEnrolled(true)
      setOfflineUnlocked(true)
    },
    [user],
  )

  const changeOfflinePin = useCallback(
    async (oldPin: string, newPin: string) => {
      if (!user) throw new Error('Sign in before changing your offline PIN.')
      await changePin(offlineRuntime.db, user.id, oldPin, newPin) // throws on wrong old PIN
    },
    [user],
  )

  const disableOfflineEncryption = useCallback(async () => {
    if (!user) throw new Error('Sign in before disabling offline encryption.')
    if (requireOfflineEncryption) {
      throw new Error('Your company requires offline encryption — it cannot be turned off.')
    }
    await removeVaultUser(offlineRuntime.db, user.id)
    offlineRuntime.setCodec(undefined)
    // Keep queued outbox work — only the encrypted read/draft partition goes.
    await offlineRuntime.clearPartition({ dropOutbox: false })
    setOfflineEnrolled(false)
    setOfflineUnlocked(false)
  }, [user, requireOfflineEncryption])

  const switchUser = useCallback(
    async (userId: string, pin: string) => {
      const result = await unlockUser(offlineRuntime.db, userId, pin) // throws on wrong PIN
      // Drop the outgoing user's live token/session — but NOT their vault
      // entry or persisted identity — before applying the new one.
      clearAuth()
      const identity: StoredIdentity = {
        ...identityFromVault(result.record),
        storedAt: new Date().toISOString(),
      }
      applyOfflineIdentity(identity)
      offlineRuntime.setCodec(result.codec)
      offlineRuntime.setUser({ userId: identity.userId, companyId: identity.companyId })
      storeIdentity(identity)
      setOfflineEnrolled(true)
      setOfflineUnlocked(true)
    },
    [clearAuth, applyOfflineIdentity],
  )

  const listDeviceUsers = useCallback(async () => listVaultUsers(offlineRuntime.db), [])

  const sdkClient = useMemo(
    () => createClient({
      baseUrl: apiBaseUrl(),
      getToken: () => tokenRef.current,
      credentials: 'include',
      onUnauthorized: async () => {
        if (impersonationRef.current) {
          clearAuth()
          return false
        }
        return refresh()
      },
      onResponse: (response) => { void trackDbOutage(response) },
    }),
    [clearAuth, refresh],
  )

  const authFetch = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const impersonation = impersonationRef.current
      const method = (init.method ?? 'GET').toUpperCase()
      if (impersonation && method !== 'GET' && method !== 'HEAD') {
        return new Response(
          JSON.stringify({
            error: {
              code: 'IMPERSONATION_READ_ONLY',
              message: 'Platform impersonation is strictly read-only.',
            },
          }),
          { status: 403, headers: { 'content-type': 'application/json' } },
        )
      }
      return sdkClient.raw(path, init)
    },
    [sdkClient],
  )

  const value = useMemo<AuthState>(
    () => ({
      status,
      ...(user ? { user } : {}),
      permissions,
      ...(companyId ? { companyId } : {}),
      signedOut,
      offlineAuthenticated,
      twoFactorSetupRequired,
      emailVerificationRequired,
      offlineEncryption: { enrolled: offlineEnrolled, unlocked: offlineUnlocked },
      requireOfflineEncryption,
      applyCompanyPolicy,
      refreshSession: refresh,
      ...(impersonationRef.current ? { impersonation: impersonationRef.current.impersonation } : {}),
      login,
      registerCompany,
      acceptInvite,
      completeMfa,
      logout,
      endImpersonation,
      enableOfflineEncryption,
      unlockOffline,
      changeOfflinePin,
      disableOfflineEncryption,
      switchUser,
      listDeviceUsers,
      authFetch,
    }),
    [
      status,
      user,
      permissions,
      companyId,
      signedOut,
      offlineAuthenticated,
      twoFactorSetupRequired,
      emailVerificationRequired,
      offlineEnrolled,
      offlineUnlocked,
      requireOfflineEncryption,
      applyCompanyPolicy,
      refresh,
      login,
      registerCompany,
      acceptInvite,
      completeMfa,
      logout,
      endImpersonation,
      enableOfflineEncryption,
      unlockOffline,
      changeOfflinePin,
      disableOfflineEncryption,
      switchUser,
      listDeviceUsers,
      authFetch,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/** UI permission gate — UX only; the API check is the security boundary. */
export function useCan(permission: string): boolean {
  return useAuth().permissions.has(permission)
}
